import {
    ReportBuilder,
    ReportBuilderCell,
    ReportBuilderCellDisplay,
    ReportBuilderCellFunctionArgument,
    ReportBuilderCellVariable,
    ReportBuilderParserData,
    ReportBuilderRow,
} from "./s25.document.report.builder.table.component";
import { S25Util } from "../../../util/s25-util";
import { InferSignal, PartialBy } from "../../../pojo/Util";
import { signal } from "@angular/core";
import { VariableWithOptions } from "../../s25-dropdown/single-select/s25.bpe.vars.dropdown.component";
import { BpeVarsConst } from "../../bpe/s25.bpe.const";
import { Doc } from "./s25.document.const";
import { BpeVariableData } from "../../bpe/bpe.vars.component";
import { Item } from "../../../pojo/Item";
import { RoleService } from "../../../services/role.service";
import { CustomAttributeService } from "../../../services/custom.attribute.service";
import { Document, DocumentListItem, DocumentService } from "../../../services/document.service";
import { DocumentUtil } from "./s25.document.util";
import { StylesUtil } from "../../s25-styles/s25.styles.util";
import { DocumentReportBuilderComponent } from "./s25.document.report.builder.component";
import { type Report } from "../../../pojo/Report";
import { DropDownItem } from "../../../pojo/DropDownItem";
import { S25Assert } from "../../../util/s25.assert";

export namespace ReportBuilderUtil {
    export function getBreakpoints(rows: ReportBuilderRow[]): number[] {
        let breakpoints = rows.flatMap((row) => {
            let sum = 0;
            return row.cells().flatMap((cell) => {
                if (cell.variable().type() === "subreport" && cell.variable().subreport) {
                    // Get breakpoints for rendered subreports
                    const bodyBreakpoints = getBreakpoints(cell.variable().subreport.body());
                    const headerBreakpoints = getBreakpoints(cell.variable().subreport.header());
                    const factor = cell.width() / 100;
                    const breakpoints = bodyBreakpoints.concat(headerBreakpoints).map((bp) => sum + bp * factor);
                    sum += cell.width();
                    return breakpoints;
                }
                sum += cell.width();
                return sum;
            });
        });
        breakpoints = S25Util.array.unique(breakpoints);
        breakpoints = breakpoints.filter((bp) => bp > 0 && bp < 100);
        breakpoints.sort((a, b) => a - b);
        return breakpoints;
    }

    export function newCell(options?: {
        [K in keyof ReportBuilderCell]?: InferSignal<ReportBuilderCell[K]>;
    }): ReportBuilderCell {
        const cell: ReportBuilderCell = {
            section: signal(options?.section ?? "header"),
            label: signal(options?.label ?? ""),
            variable: signal(
                options?.variable ?? {
                    type: signal("variable"),
                    state: signal({ options: {} }),
                    arguments: signal([]),
                    subreportId: undefined,
                    subreport: undefined,
                },
            ),
            css: signal(options?.css ?? ""),
            width: signal(options?.width ?? 100),
            display: signal(options?.display ?? []),
            valid: signal(options?.valid ?? true),
            errorMessage: signal(options?.errorMessage),
            errors: signal(options?.errors ?? {}),
        };
        cell.display = signal(ReportBuilderUtil.getCellDisplayValue(cell));
        return cell;
    }

    export async function parse(tableString: string, type: Doc.ReportBuilderTypeId): Promise<ReportBuilder> {
        if (!tableString) return;
        const parserData = await getParserData();
        return parseWithData({ tableString, type }, parserData);
    }

    export async function getParserData() {
        const [eventRoles, eventAttributes, templateList] = await Promise.all([
            RoleService.getRoles(Item.Ids.Event),
            CustomAttributeService.getCustomAttributes(Item.Ids.Event, true),
            DocumentService.getDocumentTemplateList(),
        ]);

        const parserData: ReportBuilderParserData = {
            eventRole: new Map(),
            eventAttribute: new Map(),
            templates: new Map(),
        };
        for (const role of eventRoles) parserData.eventRole.set(role.role_id, role);
        for (const attribute of eventAttributes?.custom_attributes?.attribute ?? []) {
            parserData.eventAttribute.set(attribute.cust_atrb_id, attribute);
        }
        for (const template of templateList) parserData.templates.set(template.itemId, template);

        return parserData;
    }

    export function parseWithData(
        data: {
            tableString: string;
            type: Doc.ReportBuilderTypeId;
            groupBy?: string;
            pageBreak?: string;
        },
        parserData: ReportBuilderParserData | undefined,
    ): ReportBuilder {
        if (!data.tableString) return;

        // Unescape ampersands
        const tableString = data.tableString.replace(/&amp;/g, "&");

        const groups =
            data.groupBy
                ?.split(",")
                .map((group) => group.trim())
                .filter((group) => !!group) ?? [];
        const pageBreaks = new Set(data.pageBreak?.split(",").map((group) => group.trim()) ?? []);

        const match = Array.from(tableString.matchAll(Doc.tableBuilderRegex))[0];
        if (!match?.groups) {
            return { variable: signal(""), groups: signal([]), header: signal([]), body: signal([]) };
        }

        const sections = new Map<string, Map<number, ReportBuilderRow>>([
            ...groups.map((_, i) => <[string, Map<number, ReportBuilderRow>]>[`GH_${i + 1}`, new Map()]),
            ["header", new Map()],
            ["body", new Map()],
        ]);

        const getSectionRows = (section: string) => {
            return Array.from(sections.get(section)?.entries() ?? [])
                .sort((a, b) => a[0] - b[0])
                .map(([_, row]) => row);
        };

        const cellMatches = Array.from(match.groups.table.matchAll(Doc.reportBuilderColumnRegex));
        for (const cellMatch of cellMatches) {
            if (!cellMatch.groups) continue;

            const section = sections.get(cellMatch.groups.section);
            if (!section) continue;

            const groupIndex = parseInt(cellMatch.groups.section.match(/GH_(\d+)/)?.[1] ?? "") - 1;
            let group: string | undefined;
            if (!isNaN(groupIndex)) {
                group = groups[groupIndex];
            }

            const cell: ReportBuilderCell = ReportBuilderUtil.newCell({
                section: cellMatch.groups.section as InferSignal<ReportBuilderCell["section"]>,
                variable: ReportBuilderUtil.parseVariable(cellMatch.groups.variable, data.type, group, parserData),
                label: cellMatch.groups.label,
                css: cellMatch.groups.style,
                width: Number(cellMatch.groups.width),
            });

            const rowIndex = Number(cellMatch.groups.row);
            if (!section?.has(rowIndex)) section.set(rowIndex, { cells: signal([]) });
            section.get(rowIndex)!.cells().push(cell);

            ReportBuilderUtil.validateCell(cell, group, { validateCss: true });
        }

        const reportBuilder: ReportBuilder = {
            variable: signal(match.groups.variable ?? "ws.flexTable.reservations"),
            groups: signal(
                groups.map((group, i) => {
                    const groupVariableId = group.match(/\.(-?\d+)/)?.[1];
                    const groupVal = group.replace(/\.-?\d+/, ".$id");
                    const groupVariable = BpeVarsConst.Items.occurrencesGroupBy.find((group) => group.val === groupVal);
                    return {
                        section: signal(`GH_${i + 1}`),
                        groupBy: signal(group),
                        rows: signal(getSectionRows(`GH_${i + 1}`)),
                        pageBreak: signal(pageBreaks.has(group)),
                        state: signal({
                            variable: <VariableWithOptions>groupVariable,
                            options: { id: groupVariableId },
                        }),
                    };
                }),
            ),
            header: signal(getSectionRows("header")),
            body: signal(getSectionRows("body")),
        };
        if (reportBuilder.groups().length < DocumentReportBuilderComponent.MAX_GROUPS) {
            reportBuilder.groups().push({
                section: signal(`GH_${reportBuilder.groups().length + 1}`),
                groupBy: signal(undefined),
                rows: signal([]),
                pageBreak: signal(false),
                state: signal({ options: {} }),
            });
        }

        return reportBuilder;
    }

    export function parseVariable(
        variable: string,
        type: Doc.ReportBuilderTypeId,
        groupBy: string | undefined,
        parserData: ReportBuilderParserData | undefined,
    ): ReportBuilderCellVariable {
        // Replace any specific ids with the generic placeholder
        groupBy = groupBy?.replace(/\.-?\d+/, ".$id");

        const ret: ReportBuilderCellVariable = {
            type: signal("variable"),
            state: signal({ variable: undefined, options: {}, var: variable }),
            arguments: signal([]),
            subreportId: undefined,
            subreport: undefined,
        };

        const match =
            variable?.match(/^(?<variable>(\d+(\.\d+)?)|([^.\s|]+))(?:\.(?<id>-?\d+))?(?:\s*\|\s*(?<format>\w+))?/)
                ?.groups ?? {};
        const id = Number(match.id);
        if (id) ret.state().options.id = { val: id };
        if (match.format) {
            ret.state().format = BpeVarsConst.Items.wsDateFormats.find((format) => format.val === match.format);
        }

        const variableName = match.variable?.match(/^\s*([^.\s|(]+)/)?.[1];
        let variables: VariableWithOptions[] = BpeVarsConst.Items[mapBpeVariablesKey(type)].concat(
            BpeVarsConst.Items.reportBuilderFunctions,
        ) as VariableWithOptions[];
        variables = variables.filter((variable) => {
            const groups = variable.reportBuilder?.groups;
            return !groupBy || !groups || groups.includes(groupBy);
        });
        ret.state().variable = variables.find((v) => v.val?.startsWith(variableName));

        const isFunction = /^\s*\w+\(.*\)\s*$/.test(variable);
        if (isFunction) {
            ret.type.set("function");
            const func = variable.split("(")[0];
            const spec = Doc.reportBuilderFunctions[func];
            ret.arguments.set(
                variable
                    .match(/[^(]+\((.*)\)\s*$/)[1]
                    .split(",")
                    .map((variable, i) => {
                        const parsed = parseVariable(variable, type, groupBy, parserData);

                        const { arg } = ReportBuilderUtil.getArgumentDefinition(spec, parsed.state(), i);

                        switch (arg.type) {
                            case "text":
                            case "comparisonOperator":
                                return { ...arg, text: variable, error: signal(undefined) };
                            case "variable":
                                return { ...arg, state: parsed.state, error: signal(undefined) };
                            case "number":
                                return { ...arg, number: Number(variable), error: signal(undefined) };
                            case "date":
                            case "dateTime":
                                return { ...arg, date: S25Util.date.parseDropTZ(variable), error: signal(undefined) };
                            case "multi":
                                S25Assert(false, "Should not be parsed to multi-argument");
                            default:
                                S25Assert.never(arg.type, "Uncovered function argument type");
                        }
                    }),
            );
        }

        const isSubreport = /^\s*subreport\.-?\d+/.test(variable);
        if (isSubreport) {
            ret.type.set("subreport");
            ret.state().variable = { val: variable };
            ret.subreportId = Number(variable.split(".")[1]);
            if (ret.subreportId < 0) {
                ret.state().variable.txt = `Template - ${parserData.templates.get(ret.subreportId).itemName}`;
            }
        }

        const options = ret.state().variable?.options;
        if (!!options?.length) {
            if (options.some(DocumentUtil.isEventRole)) {
                ret.state().options.id.txt = parserData?.eventRole?.get(id)?.role_name;
            } else if (options.some(DocumentUtil.isEventAttribute)) {
                ret.state().options.id.txt = parserData?.eventAttribute?.get(id)?.cust_atrb_name;
            }
        }

        return ret;
    }

    export function getArgumentDefinition(
        func: Doc.ReportBuilderFunctionDefinition,
        state: BpeVariableData,
        index: number,
        returnMulti?: boolean,
    ): { arg: Doc.ReportBuilderFunctionArgument; type: "fixed" | "variable" } {
        // Find fixed argument definition
        if (func.fixedArguments?.[index]) {
            const arg = func.fixedArguments[index];

            if (arg.type === "multi") {
                if (returnMulti) return { arg, type: "fixed" };
                return { arg: ReportBuilderUtil.getArgumentDefinitionFromMulti(arg, state), type: "fixed" };
            }
            return { arg, type: "fixed" };
        }
        // Find variable argument definition
        for (const arg of func.variableArguments ?? []) {
            if (arg.type === "multi") {
                const def = ReportBuilderUtil.getArgumentDefinitionFromMulti(arg, state);
                if (def) {
                    if (returnMulti) return { arg, type: "variable" };
                    return { arg: def, type: "variable" };
                }
            }

            if (!state?.variable && arg.type === "text") return { arg, type: "variable" };
            if (
                state.variable &&
                arg.type === "variable" &&
                (!arg.variableTypes || arg.variableTypes.includes(state.variable.type))
            ) {
                return { arg, type: "variable" };
            }
        }
    }

    export function getArgumentDefinitionFromMulti(arg: Doc.ReportBuilderFunctionArgument, state: BpeVariableData) {
        if (!state?.variable) {
            let option: Doc.ReportBuilderFunctionArgument["options"][number];
            if (/^\d+(\.\d+)?$/.test(state.var)) {
                option = arg.options.find((opt) => opt.type === "number");
            }
            if (!option && /^\d{4}-\d{2}-\d{2}$/.test(state.var)) {
                option = arg.options.find((opt) => opt.type === "date");
            }
            if (!option && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/.test(state.var)) {
                option = arg.options.find((opt) => opt.type === "dateTime");
            }
            if (!option) {
                option = arg.options.find((opt) => opt.type === "text");
            }
            return option;
        }
        return arg.options.find((opt) =>
            opt.variableTypes ? opt.variableTypes.includes(state.variable.type) : opt.type === "variable",
        );
    }

    export function getCellDisplayValue(cell: ReportBuilderCell): ReportBuilderCellDisplay[] {
        const display: ReportBuilderCellDisplay[] = [];
        if (cell.label()) display.push({ type: "text", value: cell.label() });
        if (cell.variable().type() === "variable") {
            display.push(getCellVariableDisplayValue(cell.variable().state()));
        } else if (cell.variable().type() === "subreport") {
            display.push({ type: "subreport", value: null });
        } else if (cell.variable().type() === "function" && !!cell.variable().state().variable) {
            display.push(...getCellFunctionDisplayValue(cell));
        }

        return display;
    }

    export function getCellVariableDisplayValue(
        state: BpeVariableData,
        options?: DisplayOptions,
    ): ReportBuilderCellDisplay {
        const { hideFormat } = options ?? {};
        if (!state?.variable) return { type: "text", value: "" };

        let value = state.variable.txt ?? state.variable.val;
        if (state.variable.hasFormat && !hideFormat) {
            value += ` (${state.format?.txt || "no format"})`;
        }
        if (state.variable.options?.length) {
            value += ` (${state.options.id?.txt ?? "None"})`;
        }

        if (state.variable.type === "image" && state.options.id?.val) {
            return {
                type: "image",
                value: `{{${value}}}`,
                image: { id: state.options.id!.val, width: state.options.width },
            };
        }

        return { type: "variable", value: `{{${value}}}` };
    }

    export function getCellFunctionDisplayValue(cell: ReportBuilderCell): ReportBuilderCellDisplay[] {
        const func = cell.variable().state().variable.val;
        const args = cell.variable().arguments();
        switch (func) {
            case "concat": {
                return args.map((arg) => getCellFunctionArgumentDisplayValue(arg));
            }
            case "dateCompare":
            case "dateTimeCompare":
            case "numberCompare":
            case "stringCompare": {
                const [var1, var2, op, ifTrue, ifFalse] = cell.variable().arguments();
                const display: ReportBuilderCellDisplay[] = [];

                if (var1?.type === "variable" && !var1?.state!().variable) return display;
                display.push(
                    { type: "functionText", value: "IF " },
                    ReportBuilderUtil.getCellFunctionArgumentDisplayValue(var1, { hideFormat: true }),
                );

                if (!op.text) return display;
                display.push({
                    type: "functionText",
                    value: ` ${Doc.operatorMap[op.text as keyof typeof Doc.operatorMap]} `,
                });

                if (var2?.type === "variable" && !var2?.state!().variable) return display;
                display.push(ReportBuilderUtil.getCellFunctionArgumentDisplayValue(var2, { hideFormat: true }));

                if (ifTrue?.type === "variable" && !ifTrue?.state!().variable) return display;
                display.push(
                    { type: "functionText", value: " THEN " },
                    ReportBuilderUtil.getCellFunctionArgumentDisplayValue(ifTrue),
                );

                if (ifFalse?.type === "variable" && !ifFalse?.state!().variable) return display;
                display.push(
                    { type: "functionText", value: " ELSE " },
                    ReportBuilderUtil.getCellFunctionArgumentDisplayValue(ifFalse),
                );

                return display;
            }
            case "isNull": {
                const [var1, ifTrue, ifFalse] = cell.variable().arguments();
                const display: ReportBuilderCellDisplay[] = [];

                if (!var1?.state().variable) return display;
                display.push(
                    { type: "functionText", value: "IF " },
                    ReportBuilderUtil.getCellVariableDisplayValue(var1.state(), { hideFormat: true }),
                    { type: "functionText", value: " IS NULL " },
                );

                if (ifTrue?.type === "variable" && !ifTrue?.state().variable) return display;
                display.push(
                    { type: "functionText", value: " THEN " },
                    ReportBuilderUtil.getCellFunctionArgumentDisplayValue(ifTrue),
                );

                if (ifFalse?.type === "variable" && !ifFalse?.state().variable) return display;
                display.push(
                    { type: "functionText", value: " ELSE " },
                    ReportBuilderUtil.getCellFunctionArgumentDisplayValue(ifFalse),
                );

                return display;
            }
            case "contdCalendarTimes": {
                const [date1, date2] = cell.variable().arguments();
                const display: ReportBuilderCellDisplay[] = [];

                if (!date1?.state().variable) return display;
                display.push(ReportBuilderUtil.getCellVariableDisplayValue(date1.state(), { hideFormat: true }));

                if (!date2?.state().variable) return display;
                display.push(
                    { type: "functionText", value: " - " },
                    ReportBuilderUtil.getCellVariableDisplayValue(date2.state(), { hideFormat: true }),
                );

                return display;
            }
            case "combinePeriodTimes": {
                const [text1, date1, date2, date3] = cell.variable().arguments();
                const display: ReportBuilderCellDisplay[] = [];

                if (!date1?.state().variable) return display;
                display.push(ReportBuilderUtil.getCellVariableDisplayValue(date1.state()));

                if (!date2?.state().variable) return display;
                display.push(
                    { type: "functionText", value: " - " },
                    ReportBuilderUtil.getCellVariableDisplayValue(date2.state()),
                );

                if (!date3?.state().variable) return display;
                display.push(
                    { type: "functionText", value: " COMPARED WITH " },
                    ReportBuilderUtil.getCellVariableDisplayValue(date2.state(), { hideFormat: true }),
                );

                if (!!text1?.text) {
                    display.push({ type: "functionText", value: " ELSE " }, { type: "text", value: text1.text });
                }

                return display;
            }
        }
    }

    export function getCellFunctionArgumentDisplayValue(
        arg: ReportBuilderCellFunctionArgument,
        options?: DisplayOptions,
    ): ReportBuilderCellDisplay {
        switch (arg.type) {
            case "text":
                return { type: "text", value: arg.text ?? "" };
            case "variable":
                return ReportBuilderUtil.getCellVariableDisplayValue(arg.state!(), options);
            case "number":
                return { type: "text", value: arg.number?.toString() ?? "" };
            case "date":
                return { type: "text", value: S25Util.date.toS25ISODateStr(arg.date)! };
            case "dateTime":
                return { type: "text", value: S25Util.date.toS25ISODateTimeStrFromDate(arg.date!) };
            case "comparisonOperator":
            case "multi":
                return { type: "text", value: "" };
            default:
                S25Assert.never(arg.type, "Uncovered function argument type");
        }
    }

    export function clearVariable(cell: ReportBuilderCell) {
        const newVariable: ReportBuilderCellVariable = {
            type: signal("variable"),
            state: signal({ options: {} }),
            arguments: signal([]),
            subreportId: undefined,
            subreport: undefined,
        };
        cell.variable.set(newVariable);
        cell.display.set(ReportBuilderUtil.getCellDisplayValue(cell));
    }

    export function resizeCells(
        widths: number[],
        index: number,
        change: number,
        options?: { minCellWidth?: number; breakpoints?: number[]; snapDistance?: number },
    ): number[] {
        const growingCellIndex = change < 0 ? index + 1 : index;
        let toAdd = Math.abs(change);

        // Snap to breakpoints
        if (options?.breakpoints?.length) {
            const position = S25Util.array.count(widths.slice(0, index + 1), (w) => w) + change;
            const breakpoint = options.breakpoints.reduce((closest, bp) =>
                Math.abs(position - closest) > Math.abs(position - bp) ? bp : closest,
            );
            if (Math.abs(position - breakpoint) <= (options.snapDistance ?? 5))
                toAdd += Math.sign(change) * (breakpoint - position);
        }

        // Resize cells
        let remaining = toAdd;
        const newWidths = [...widths];
        let i = growingCellIndex + Math.sign(change);
        while (i >= 0 && i < widths.length && remaining > 0) {
            newWidths[i] = Math.max(options?.minCellWidth ?? 1, widths[i] - remaining);
            remaining -= widths[i] - newWidths[i];
            i += Math.sign(change);
        }

        newWidths[growingCellIndex] = widths[growingCellIndex] + (toAdd - remaining);

        return newWidths;
    }

    export function stringify(reportBuilder: ReportBuilder, type: Doc.ReportBuilderTypeId) {
        const rows = getAllRows(reportBuilder);
        const cells = rows.map(stringifyRow).join(" || ");

        const str = `{{for: ws.flexTable.${type}: ${cells} :end-for}}`;

        // Escape ampersands
        return str.replace(/&/g, "&amp;");
    }

    export function stringifyRow(row: ReportBuilderRow, rowIndex: number) {
        return row
            .cells()
            .map((cell) => stringifyCell(cell, rowIndex))
            .join(" || ");
    }

    export function stringifyCell(cell: ReportBuilderCell, row: number) {
        return `${cell.section()} ^ ${row + 1} ^ ${cell.label() ?? ""} ^ ${stringifyVariable(cell.variable()) ?? ""} ^ ${cell.css() ?? ""} ^ ${cell.width().toFixed(1)}`;
    }

    export function stringifyVariable(variable: ReportBuilderCellVariable) {
        if (variable.type() === "variable") return variable.state().var;
        if (variable.type() === "subreport") return variable.state().var;
        if (variable.type() === "function" && !!variable.state().variable) {
            const args = variable.arguments().map(stringifyFunctionArgument);
            return `${variable.state().variable.val}(${args.join(",")})`;
        }
        return "";
    }

    export function stringifyFunctionArgument(arg: ReportBuilderCellFunctionArgument): string {
        switch (arg.type) {
            case "text":
            case "comparisonOperator":
                return arg.text!;
            case "variable":
                return arg.state!().var!;
            case "number":
                return arg.number?.toString() ?? "";
            case "date":
                return S25Util.date.toS25ISODateStr(arg.date)!;
            case "dateTime":
                return S25Util.date.toS25ISODateTimeStrFromDate(arg.date!);
            case "multi":
                return "";
            default:
                S25Assert.never(arg.type, "Uncovered function argument type");
        }
    }

    export function getSubreportIds(tableString: string, type: Doc.ReportBuilderTypeId): number[] {
        const builder = parseWithData({ tableString, type }, undefined);
        const rows = ReportBuilderUtil.getAllRows(builder);
        const subreports = rows
            .flatMap((row) => row.cells())
            .flatMap((cell) => cell.variable().subreportId)
            .filter((id) => id);
        return S25Util.array.unique(subreports);
    }

    export function mapSubreports(document: PartialBy<Document, "document_id">, map: Record<number, number>) {
        if (!DocumentUtil.canReferenceDocuments(document)) return;

        const builder = parseWithData(
            {
                tableString: document.body_text,
                type: document.document_type as Doc.ReportBuilderTypeId,
                pageBreak: document.page_break,
                groupBy: document.group_by,
            },
            undefined,
        );

        const rows = ReportBuilderUtil.getAllRows(builder);
        for (const row of rows) {
            for (const cell of row.cells()) {
                if (cell.variable().type() !== "subreport") continue;
                cell.variable().state().var = `subreport.${map[cell.variable().subreportId]}`;
            }
        }

        document.body_text = ReportBuilderUtil.stringify(builder, document.document_type as Doc.ReportBuilderTypeId);
    }

    export function isFunctionArgumentEmpty(arg: ReportBuilderCellFunctionArgument): boolean {
        switch (arg.type) {
            case "text":
            case "comparisonOperator":
                return arg.text === "";
            case "variable":
                return !arg.state!().variable;
            case "number":
                return arg.number === undefined || arg.number === null;
            case "date":
            case "dateTime":
                return !S25Util.isDefined(arg.date);
            case "multi":
                return true;
            default:
                S25Assert.never(arg.type, "Uncovered function argument type");
        }
    }

    function validateVariableState(state: BpeVariableData, groupBy: string | undefined): string | undefined {
        const groups = state.variable?.reportBuilder?.groups;
        if (groupBy && !!groups && !groups.includes(groupBy)) {
            // Hack to make it so that the dropdown doesn't match it to an existing item
            state.variable = { ...state.variable, itemId: "", val: "" };
            return "Variable is not available for this group by choice";
        }
    }

    export function validateCell(
        cell: ReportBuilderCell,
        groupBy: string | undefined,
        options?: { validateCss?: boolean },
    ): void {
        // Replace any specific ids with the generic placeholder
        groupBy = groupBy?.replace(/\.-?\d+/, ".$id");

        cell.valid.set(true);
        cell.errorMessage.set(undefined);
        const errors: CellErrors = {};

        if (/[\^]/.test(cell.label())) {
            errors.label = "Label cannot contain ^";
            cell.errorMessage.set("Invalid label");
            cell.valid.set(false);
        }

        if (cell.variable().type() === "variable") {
            const errMsg = validateVariableState(cell.variable().state(), groupBy);
            if (errMsg) {
                errors.variable = errMsg;
                cell.errorMessage.set("Invalid variable");
                cell.valid.set(false);
            }
        }

        if (cell.variable().type() === "function" && !!cell.variable().state().variable) {
            const args = cell.variable().arguments();
            const func = Doc.reportBuilderFunctions[cell.variable().state().variable.val];
            for (const arg of args) {
                arg.error.set(undefined);

                switch (arg.type) {
                    case "text": {
                        if (/[\^,]/.test(arg.text!)) {
                            arg.error.set("Function text cannot contain: ,^");
                        }
                        break;
                    }
                    case "variable": {
                        const errMsg = validateVariableState(arg.state!(), groupBy);
                        if (errMsg) {
                            arg.error.set(errMsg);
                        }
                        break;
                    }
                    case "number": {
                        if (!S25Util.isDefined(arg.number)) {
                            arg.error.set("Invalid number");
                        }
                        break;
                    }
                    case "date": {
                        if (!arg.date || !S25Util.date.isValid(arg.date)) {
                            arg.error.set("Invalid date");
                        }
                        break;
                    }
                    case "dateTime": {
                        if (!arg.date || !S25Util.date.isValid(arg.date)) {
                            arg.error.set("Invalid date-time");
                        }
                        break;
                    }
                    case "comparisonOperator":
                    case "multi":
                        break;
                    default:
                        S25Assert.never(arg.type, "Uncovered function argument type");
                }
                if (arg.error()) {
                    cell.errorMessage.set("Invalid function argument");
                    cell.valid.set(false);
                }
            }
            if (func.fixedArguments) {
                // Check that all fixed arguments are filled out
                for (let i = 0; i < func.fixedArguments.length; i++) {
                    const arg = args[i];
                    const spec = func.fixedArguments[i];
                    if (arg && !spec.optional && ReportBuilderUtil.isFunctionArgumentEmpty(arg)) {
                        arg.error.set(`Please select a value`);
                        cell.errorMessage.set("Missing function argument");
                        cell.valid.set(false);
                    }
                }
            }
        }

        if (cell.variable().type() === "subreport" && cell.variable().subreportId < 0) {
            errors.variable = "Template subreports need to be changed before saving";
            cell.errorMessage.set("Invalid subreport");
            cell.valid.set(false);
        }

        if (options?.validateCss) ReportBuilderUtil.validateCss(cell);

        cell.errors.set(errors);
    }

    export function validateCss(cell: ReportBuilderCell) {
        const rule = StylesUtil.parseCssRule(cell.css());
        const cssValid = !StylesUtil.validateCssRule(rule, ReportBuilderUtil.validateCssProperty).error;
        if (!cssValid) {
            cell.errorMessage.set("Invalid styles");
            cell.valid.set(false);
        }
    }

    export function validateCssProperty(property: StylesUtil.CssProperty): string {
        if (!!property.property && /[\^]/.test(property.value)) return "Styles cannot contain ^";
    }

    export function getDefaultReportBuilder(): ReportBuilder {
        return {
            variable: signal(`ws.flexTable.reservations`),
            groups: signal([
                {
                    section: signal("GH_1"),
                    groupBy: signal(undefined),
                    rows: signal([]),
                    pageBreak: signal(false),
                    state: signal({ options: [] }),
                },
            ]),
            header: signal([]),
            body: signal([
                { cells: signal(new Array(4).fill(null).map((_) => ReportBuilderUtil.newCell({ section: "body" }))) },
            ]),
        };
    }

    export function getAllRows(builder: ReportBuilder) {
        return builder
            .groups()
            .flatMap((group) => group.rows())
            .concat(builder.header())
            .concat(builder.body());
    }

    export function variablesFilter(groupBy: string | undefined, items: DropDownItem[]) {
        // Filter out variables only available in specific group headers
        return items.filter((item) => {
            if (!item.reportBuilder?.groups) return true;
            return item.reportBuilder.groups.includes(groupBy!);
        });
    }

    export function mapBpeVariablesKey(key: Doc.ReportBuilderTypeId) {
        switch (key) {
            // Map "segments" to "subreportSegments" because "segments" was already taken for the segments table builder
            case "segments":
                return "subreportSegments";
            default:
                return key;
        }
    }

    export const allowedSubreports = {
        standard: <Partial<Record<keyof typeof Doc.type, Set<Doc.ReportBuilderTypeId>>>>{
            reservations: new Set([
                "rsrvReq",
                "rsrvEventCustAtrb",
                "rsrvLoc",
                "rsrvRsrc",
                "rsrvRsrcByCat",
                "rsrvOrg",
                "rsrvContact",
                "rsrvRsrcPref",
                "rsrvLocPref",
            ]),
        },
        subreport: <Partial<Record<keyof typeof Doc.type, Set<Doc.ReportBuilderTypeId>>>>{
            segments: new Set([
                "reservations",
                "rsrvEventCustAtrb",
                "rsrvOrg",
                "rsrvContact",
                "rsrvLoc",
                "rsrvRsrc",
                "rsrvRsrcByCat",
                "rsrvRsrcPref",
                "rsrvLocPref",
            ]),
            reservations: new Set([
                "rsrvEventCustAtrb",
                "rsrvOrg",
                "rsrvContact",
                "rsrvLoc",
                "rsrvRsrc",
                "rsrvRsrcByCat",
                "rsrvRsrcPref",
                "rsrvLocPref",
            ]),
            rsrvReq: new Set([]),
            rsrvEventCustAtrb: new Set([]),
            rsrvLoc: new Set([]),
            rsrvRsrc: new Set([]),
            rsrvRsrcByCat: new Set([]),
            rsrvOrg: new Set([]),
            rsrvContact: new Set([]),
            rsrvRsrcPref: new Set([]),
            rsrvLocPref: new Set([]),
        },
    };

    export function mapSubreportsToDropdownItems(
        documentType: Doc.TypeId,
        documentScope: Doc.Scope | undefined,
        subreports: DocumentListItem[],
    ) {
        // Filter available subreports based on the document type and scope
        const scope = documentScope === "subreport" ? "subreport" : "standard";
        const allowedSubreportTypes = ReportBuilderUtil.allowedSubreports[scope][documentType];
        if (allowedSubreportTypes) {
            subreports = subreports.filter((subreport) =>
                allowedSubreportTypes.has(<Doc.SubreportTypeId>subreport.type),
            );
        }

        // Group by document type
        const groups = S25Util.array.groupBy(subreports, (subreport) => subreport.type);
        const newItems: DropDownItem[] = S25Util.objectKeys(groups).flatMap((type) =>
            BpeVarsConst.group(
                Doc.type[type].label,
                groups[type]!.map((subreport) => ({ val: `subreport.${subreport.itemId}`, txt: subreport.itemName })),
            ),
        );

        return newItems;
    }

    type DisplayOptions = { hideFormat?: boolean };
    export type CellErrors = { label?: string; variable?: string };

    export namespace ReservationsMigration {
        export const groupByRegex = /\{\{\s*ws\.groupBy\s*(\|\s*\w+\s*)?}}/i;
    }
}
